import { ApiError, queryKeys, type StoreSettingsInput } from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

/** Lakewood's clock, whatever the staff member's own computer says. */
function atStore(iso: string | null): string {
  if (!iso) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

const REASON_LABEL = { shabbos: 'Shabbos', yom_tov: 'Yom Tov', manual: 'paused by staff' } as const

/**
 * Closes the kiosks and the website's checkout from candle-lighting until
 * motzei on Shabbos and Yom Tov (t1130). Times are Lakewood's.
 */
export function ShabbosModeCard() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.storeSettings(),
    queryFn: () => api.get<StoreSettingsInput>('/settings/store'),
  })

  const [enabled, setEnabled] = useState(true)
  const [candle, setCandle] = useState('18')
  const [motzei, setMotzei] = useState('72')

  useEffect(() => {
    if (!data) return
    setEnabled(data.shabbosModeEnabled)
    setCandle(String(data.shabbosCandleLightingOffsetMinutes))
    setMotzei(String(data.shabbosMotzeiOffsetMinutes))
  }, [data])

  const save = useMutation({
    mutationFn: (patch: Partial<StoreSettingsInput>) => api.patch('/settings/store', patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() })
      toast.success('Saved', 'Kiosks and the website follow this within a minute.')
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const candleN = Number(candle)
  const motzeiN = Number(motzei)
  const candleInvalid = !Number.isInteger(candleN) || candleN < 0 || candleN > 120
  const motzeiInvalid = !Number.isInteger(motzeiN) || motzeiN < 0 || motzeiN > 180
  const unchanged = data
    ? enabled === data.shabbosModeEnabled &&
      candleN === data.shabbosCandleLightingOffsetMinutes &&
      motzeiN === data.shabbosMotzeiOffsetMinutes
    : true

  const status = data?.shabbosStatus
  const nextChange = status?.closed ? status.closedUntil : status?.nextCloseAt

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Shabbos and Yom Tov</CardTitle>
        <CardDescription>
          Closes the kiosks, and checkout on the website, from candle-lighting until motzei. Times
          are for Lakewood. Customers can still browse and fill a cart online.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={status.closed ? 'danger' : 'success'}>
              {status.closed ? 'Closed' : 'Open'}
            </Badge>
            <span>
              {status.closed
                ? `${status.reason ? `Closed for ${REASON_LABEL[status.reason]}` : 'Closed'}${
                    status.closedUntil ? ` — reopens ${atStore(status.closedUntil)}` : ''
                  }`
                : status.nextCloseAt
                  ? `Open — next closes ${atStore(status.nextCloseAt)}`
                  : 'Open'}
            </span>
          </div>
        ) : null}

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Close on Shabbos and Yom Tov</span>
          <Switch checked={enabled} disabled={isLoading} onCheckedChange={setEnabled} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Closes before sunset (minutes)"
            hint="Candle-lighting. Usually 18."
            error={candleInvalid ? 'Whole number, 0 to 120' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                max={120}
                inputMode="numeric"
                disabled={isLoading}
                value={candle}
                invalid={candleInvalid}
                onChange={(e) => setCandle(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Reopens after sunset (minutes)"
            hint="Motzei. 72 is the strictest common time."
            error={motzeiInvalid ? 'Whole number, 0 to 180' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                max={180}
                inputMode="numeric"
                disabled={isLoading}
                value={motzei}
                invalid={motzeiInvalid}
                onChange={(e) => setMotzei(e.target.value)}
              />
            )}
          </Field>
        </div>

        <Button
          variant="primary"
          onClick={() =>
            save.mutate({
              shabbosModeEnabled: enabled,
              shabbosCandleLightingOffsetMinutes: candleN,
              shabbosMotzeiOffsetMinutes: motzeiN,
            })
          }
          disabled={candleInvalid || motzeiInvalid || unchanged || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>

        <div className="space-y-2 border-t border-border pt-4">
          <p className="text-sm font-medium">Override</p>
          {status?.overrideActive ? (
            <>
              <Alert tone="warning">
                {data?.shabbosOverride === 'open' ? 'Forced open' : 'Forced closed'}
                {status.overrideExpiresAt
                  ? `, until ${atStore(status.overrideExpiresAt)}. The calendar takes over again then.`
                  : '.'}
              </Alert>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => save.mutate({ shabbosOverride: null })}
                disabled={save.isPending}
              >
                Clear override
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-foreground-muted">
                Overrides the calendar until the next change
                {nextChange ? ` (${atStore(nextChange)})` : ''}, then the calendar takes over again.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => save.mutate({ shabbosOverride: 'open' })}
                  disabled={save.isPending || isLoading}
                >
                  Force open
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => save.mutate({ shabbosOverride: 'closed' })}
                  disabled={save.isPending || isLoading}
                >
                  Force closed
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
